import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { RelationshipType, SpaceService } from "../../services/space.service";
import { S25Util } from "../../util/s25-util";
import { Item } from "../../pojo/Item";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { ResourceMicroService } from "../../services/resource.micro.service";

@TypeManagerDecorator("s25-ng-object-details-relationships")
@Component({
    selector: "s25-ng-object-details-relationships",
    template: `
        @if (!relationships.length) {
            none
        }
        <div class="obj-container c-margin-bottom--half">
            @for (rel of relationships; track $index) {
                @let relationshipType = rel[0];
                @let relTypeProp = rel[1];
                @let assocObjects = rel[2];
                @if ($index !== 0) {
                    <div class="obj-divider c-margin-top--half"></div>
                }
                <label class="c-margin-top--quarter">
                    <span class="type-label">{{ relationshipType }}</span>
                    @for (obj of assocObjects; track obj.itemId) {
                        <div class="obj-wrapper">
                            @if (canEdit) {
                                <s25-ng-icon
                                    [type]="'close'"
                                    (click)="deleteItem(+obj.itemId, relTypeProp)"
                                    (keydown.enter)="deleteItem(+obj.itemId, relTypeProp)"
                                    role="button"
                                    tabindex="0"
                                ></s25-ng-icon>
                            }
                            <s25-item-generic
                                [includeTypeIcon]="true"
                                [modelBean]="{ itemTypeId: itemTypeId, itemId: +obj.itemId }"
                                [hideLabel]="true"
                            ></s25-item-generic>
                        </div>
                    }
                    @if (relTypes()[relTypeProp]?.showDropdown) {
                        <s25-ng-dropdown-multi-search-criteria
                            [type]="objType"
                            [staticItems]="[{ itemId: 0, itemName: '', favorite: 'T' }]"
                            (done)="addObjects($event, relTypeProp)"
                        ></s25-ng-dropdown-multi-search-criteria>
                    }
                </label>
            }
        </div>
        <s25-loading-inline [model]="{ text: 'Saving...' }"></s25-loading-inline>
        @if (canEdit) {
            <label class="c-margin-top--quarter add-type-label">
                <span class="type-label">Add Relationship</span>
                <s25-generic-dropdown
                    [items]="typeOptions[objType]"
                    (chosenChange)="toggleObjDropdown($event)"
                ></s25-generic-dropdown>
            </label>
        }
    `,
    styles: `
        :host {
            display: block;
            padding: 0.5rem 1rem 1rem 1rem;

            .obj-container {
                display: flex;
                flex-wrap: wrap;
                gap: 2%;

                .obj-divider {
                    border-left: 1px solid rgba(0, 0, 0, 0.15);
                }

                label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    width: fit-content;

                    .obj-wrapper {
                        display: flex;
                    }
                }
            }

            .add-type-label {
                width: 100%;
            }

            .type-label {
                font-weight: 600;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectDetailsRelationshipsComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) relationships: [string, RelationshipType, S25ItemI[]][];
    @Input({ required: true }) objType: "locations" | "resources";

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {}

    relTypes = signal<RelMap>({} as RelMap);
    typeOptions = {
        locations: [
            { itemName: "Also Assign", itemId: "alsoAssign" },
            { itemName: "Blocked By", itemId: "blockedBy" },
            { itemName: "Close To", itemId: "closeTo" },
            { itemName: "Divides Into", itemId: "dividesInto" },
            { itemName: "Subdivision Of", itemId: "subdivisionOf" },
        ],
        resources: [
            { itemName: "Also Assign", itemId: "alsoAssign" },
            { itemName: "Substitute With", itemId: "substituteWith" },
        ],
    };
    objectIdProp: "spaceId" | "resourceId";
    objectIdLists: Record<RelationshipType, Set<number>> = {} as Record<RelationshipType, Set<number>>; // keep track of object ids so duplicates aren't added

    ngOnInit() {
        this.relationships.forEach(([, relType, assocObjects]) => {
            this.objectIdLists[relType] = new Set([...assocObjects.map((obj) => +obj.itemId)]);
            this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
        });
        this.objectIdProp = this.itemTypeId === Item.Ids.Location ? "spaceId" : "resourceId";
    }

    async deleteItem(itemId: number, typeProp: RelationshipType) {
        S25LoadingApi.init(this.el.nativeElement);
        let updateMethod;
        const payload = {
            id: this.itemId,
            remove: {
                relationships: {
                    [typeProp]: [{ [this.objectIdProp]: itemId }],
                },
            },
        };
        switch (this.objType) {
            case "locations": {
                updateMethod = SpaceService.microUpdate(payload);
                break;
            }
            case "resources": {
                updateMethod = ResourceMicroService.saveResource(payload as any);
                break;
            }
        }
        const [_, error] = await S25Util.Maybe(updateMethod);
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return console.error(error);
        this.objectIdLists[typeProp]?.has(itemId) && this.objectIdLists[typeProp].delete(itemId);
        this.relationships = this.relationships
            .map((rel) => {
                // filter out object being deleted from relationship type list
                const [relName, relType, assocObjects] = rel;
                if (relType === typeProp) {
                    const updatedObjects = assocObjects.filter((obj) => obj.itemId !== itemId);
                    !updatedObjects.length &&
                        this.relTypes.update((prev) => ({
                            ...prev,
                            [relType]: null,
                        }));
                    return [relName, relType, updatedObjects];
                }
                return rel;
            })
            .filter((rel) => {
                const [, , assocObjects] = rel;
                return !!assocObjects.length;
            }) as [string, RelationshipType, S25ItemI[]][];
        this.cd.markForCheck();
    }

    toggleObjDropdown({ itemId, itemName }: DropDownItem) {
        this.relTypes.update((prev) => {
            const type = itemId as RelationshipType;
            const newObj = {} as RelMap;
            for (const relName in prev) {
                //hide other type dropdowns when one is added for cleaner UI
                const relType = relName as RelationshipType;
                if (prev[relType])
                    newObj[relType] = {
                        showDropdown: relType === type ? !prev[relType].showDropdown : relType === type,
                    };
            }
            this.relationships = this.relationships.filter((rel) => {
                const [, relKey, assocObjects] = rel;
                const hasObjects = !!assocObjects.length;
                if (!hasObjects && prev[relKey]) newObj[relKey] = null;
                return hasObjects;
            }); //if new type added but no objects added, remove when adding new type
            if (!prev[type]) {
                // if the type doesn't exist, add it
                this.relationships.push([itemName, type, []]);
                return { ...newObj, [type]: { showDropdown: true } };
            }
            return newObj;
        });
    }

    async addObjects(objects: DropDownItem[], relType: RelationshipType) {
        objects = objects.filter((obj) => !this.objectIdLists[relType]?.has(+obj.itemId));
        if (!objects?.length) {
            this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
            return;
        }
        S25LoadingApi.init(this.el.nativeElement);
        const payload = {
            id: this.itemId,
            relationships: {
                [relType]: objects.map((obj) => ({ [this.objectIdProp]: obj.itemId })),
            },
        };
        let microUpdate;
        switch (this.objType) {
            case "locations": {
                microUpdate = SpaceService.microUpdate(payload);
                break;
            }
            case "resources": {
                microUpdate = ResourceMicroService.saveResource(payload);
                break;
            }
        }
        const [_, error] = await S25Util.Maybe(microUpdate);
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return console.error(error);
        this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
        this.objectIdLists[relType] = new Set([
            ...(this.objectIdLists?.[relType] ?? []),
            ...objects.map((obj) => +obj.itemId),
        ]);
        this.relationships = this.relationships.map((rel) => {
            const [relName, relTypeProp, assocObjects] = rel;
            if (relTypeProp === relType) {
                // add new objects to the existing relationship type
                const newObjects = assocObjects.concat(objects.map((obj) => ({ itemId: obj.itemId })));
                return [relName, relTypeProp, newObjects];
            }
            return rel;
        });
        this.cd.markForCheck();
    }
}

type RelMap = Record<RelationshipType, { showDropdown: boolean }>;
